import React, { useCallback, useMemo, useState } from "react";
import { FaArrowRight, FaSearch, FaSlidersH } from "react-icons/fa";

import { filterSettingsTabs } from "../utils/settingsDashboardUtils";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const CATEGORY_THEMES = {
  "structure club": {
    label: "Structure",
    card: "from-blue-50 via-indigo-50 to-white dark:from-blue-950/40 dark:via-indigo-950/30 dark:to-slate-900",
    border: "border-blue-100 dark:border-blue-900/50",
    icon: "bg-blue-600 text-white",
    badge: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-200",
    active:
      "border-blue-500 bg-gradient-to-br from-blue-600 via-indigo-600 to-blue-800 text-white shadow-[0_22px_45px_-28px_rgba(37,99,235,0.9)]",
  },
  "identité du club": {
    label: "Identité",
    card: "from-emerald-50 via-teal-50 to-white dark:from-emerald-950/40 dark:via-teal-950/30 dark:to-slate-900",
    border: "border-emerald-100 dark:border-emerald-900/50",
    icon: "bg-emerald-600 text-white",
    badge:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-200",
    active:
      "border-emerald-500 bg-gradient-to-br from-emerald-600 via-teal-600 to-emerald-800 text-white shadow-[0_22px_45px_-28px_rgba(16,185,129,0.9)]",
  },
  "site internet": {
    label: "Site",
    card: "from-violet-50 via-purple-50 to-white dark:from-violet-950/40 dark:via-purple-950/30 dark:to-slate-900",
    border: "border-violet-100 dark:border-violet-900/50",
    icon: "bg-violet-600 text-white",
    badge:
      "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-200",
    active:
      "border-violet-500 bg-gradient-to-br from-violet-600 via-purple-600 to-violet-800 text-white shadow-[0_22px_45px_-28px_rgba(124,58,237,0.9)]",
  },
  inscription: {
    label: "Inscription",
    card: "from-amber-50 via-orange-50 to-white dark:from-amber-950/40 dark:via-orange-950/30 dark:to-slate-900",
    border: "border-amber-100 dark:border-amber-900/50",
    icon: "bg-amber-500 text-white",
    badge:
      "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-200",
    active:
      "border-amber-500 bg-gradient-to-br from-amber-500 via-orange-500 to-amber-700 text-white shadow-[0_22px_45px_-28px_rgba(245,158,11,0.9)]",
  },
  communication: {
    label: "Communication",
    card: "from-rose-50 via-pink-50 to-white dark:from-rose-950/40 dark:via-pink-950/30 dark:to-slate-900",
    border: "border-rose-100 dark:border-rose-900/50",
    icon: "bg-rose-600 text-white",
    badge: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-200",
    active:
      "border-rose-500 bg-gradient-to-br from-rose-600 via-pink-600 to-rose-800 text-white shadow-[0_22px_45px_-28px_rgba(225,29,72,0.9)]",
  },
  maintenance: {
    label: "Maintenance",
    card: "from-slate-100 via-slate-50 to-white dark:from-slate-800 dark:via-slate-900 dark:to-slate-950",
    border: "border-slate-200 dark:border-slate-700",
    icon: "bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900",
    badge: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
    active:
      "border-slate-500 bg-gradient-to-br from-slate-800 via-slate-700 to-slate-950 text-white shadow-[0_22px_45px_-28px_rgba(15,23,42,0.9)]",
  },
  default: {
    label: "Général",
    card: "from-indigo-50 via-blue-50 to-white dark:from-indigo-950/40 dark:via-blue-950/30 dark:to-slate-900",
    border: "border-indigo-100 dark:border-indigo-900/50",
    icon: "bg-indigo-600 text-white",
    badge:
      "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-200",
    active:
      "border-indigo-500 bg-gradient-to-br from-[#013E8E] via-indigo-600 to-blue-700 text-white shadow-[0_22px_45px_-28px_rgba(37,99,235,0.9)]",
  },
};

function normalizeThemeKey(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[’']/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase()
    .trim();
}

function getTabCategory(tab) {
  return tab?.category || tab?.group || tab?.section || "Général";
}

function getTabTheme(tab) {
  const rawCategory = getTabCategory(tab);
  const normalized = normalizeThemeKey(rawCategory);

  const directMatch = Object.keys(CATEGORY_THEMES).find(
    (key) => normalizeThemeKey(key) === normalized,
  );

  return CATEGORY_THEMES[directMatch] || CATEGORY_THEMES.default;
}

export default function SettingsTabsShell({
  tabs = [],
  activeTab,
  setActiveTab,
}) {
  const [tabSearch, setTabSearch] = useState("");

  const filteredTabs = useMemo(
    () => filterSettingsTabs(tabs, tabSearch),
    [tabs, tabSearch],
  );

  const activeItem = tabs.find((tab) => tab.key === activeTab) || tabs[0];
  const ActiveComponent = activeItem?.Component;

  const selectTabAndScroll = useCallback(
    (key) => {
      setActiveTab(key);

      window.setTimeout(() => {
        const panel = document.getElementById("settings-active-panel");

        if (panel) {
          panel.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 80);
    },
    [setActiveTab],
  );

  const searchActive = tabSearch.trim().length > 0;
  const totalTabs = tabs.length;
  const filteredCount = filteredTabs.length;

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <header className="border-b border-slate-200 bg-slate-50/80 p-4 dark:border-slate-800 dark:bg-slate-950/40 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Sections
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Paramètres
            </h2>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500 dark:text-slate-400">
              Accédez rapidement aux réglages du club grâce à des cartes
              visuelles classées par couleur.
            </p>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <span className="rounded-full bg-white px-3 py-1.5 text-xs font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-800">
              {totalTabs} rubrique{totalTabs > 1 ? "s" : ""}
            </span>

            {searchActive ? (
              <span className="rounded-full bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-700 ring-1 ring-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20">
                {filteredCount} résultat{filteredCount > 1 ? "s" : ""}
              </span>
            ) : null}
          </div>
        </div>

        <div className="mt-5 grid gap-3 lg:grid-cols-[minmax(0,1fr),320px]">
          <div className="relative">
            <input
              type="text"
              value={tabSearch}
              onChange={(event) => setTabSearch(event.target.value)}
              placeholder="Rechercher un paramètre..."
              className="h-12 w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-[#013E8E] focus:ring-4 focus:ring-blue-500/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:focus:border-blue-400 dark:focus:ring-blue-400/10"
            />

            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          </div>

          <select
            value={activeTab}
            onChange={(event) => setActiveTab(event.target.value)}
            className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:border-[#013E8E] focus:ring-4 focus:ring-blue-500/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:focus:border-blue-400 dark:focus:ring-blue-400/10"
          >
            {tabs.map((tab) => (
              <option key={tab.key} value={tab.key}>
                {tab.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-5">
          {filteredTabs.length ? (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {filteredTabs.map((tab) => {
                const { key, label, description, Icon } = tab;
                const active = activeTab === key;
                const theme = getTabTheme(tab);
                const category = getTabCategory(tab);

                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => selectTabAndScroll(key)}
                    title={description || label}
                    className={cn(
                      "group relative min-h-[132px] overflow-hidden rounded-[24px] border p-4 text-left transition-all duration-200",
                      active
                        ? theme.active
                        : cn(
                            "bg-gradient-to-br text-slate-800 hover:-translate-y-0.5 hover:shadow-lg dark:text-slate-100",
                            theme.card,
                            theme.border,
                          ),
                    )}
                  >
                    <div
                      className={cn(
                        "pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full transition",
                        active ? "bg-white/10" : "bg-white/70 dark:bg-white/5",
                      )}
                    />

                    <div className="relative flex h-full flex-col justify-between gap-4">
                      <div className="flex items-start justify-between gap-3">
                        <span
                          className={cn(
                            "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-lg shadow-sm transition",
                            active
                              ? "bg-white/15 text-white ring-1 ring-white/20"
                              : theme.icon,
                          )}
                        >
                          {Icon ? <Icon /> : <FaSlidersH />}
                        </span>

                        <span
                          className={cn(
                            "rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide",
                            active
                              ? "bg-white/15 text-white ring-1 ring-white/20"
                              : theme.badge,
                          )}
                        >
                          {category}
                        </span>
                      </div>

                      <div className="min-w-0">
                        <h3
                          className={cn(
                            "text-base font-bold leading-tight",
                            active
                              ? "text-white"
                              : "text-slate-950 dark:text-white",
                          )}
                        >
                          {label}
                        </h3>

                        {description ? (
                          <p
                            className={cn(
                              "mt-1 line-clamp-2 text-xs leading-5",
                              active
                                ? "text-white/75"
                                : "text-slate-500 dark:text-slate-400",
                            )}
                          >
                            {description}
                          </p>
                        ) : null}
                      </div>

                      <div className="flex items-center justify-between gap-3">
                        <span
                          className={cn(
                            "text-xs font-bold",
                            active
                              ? "text-white/80"
                              : "text-slate-500 dark:text-slate-400",
                          )}
                        >
                          {active ? "Onglet ouvert" : "Configurer"}
                        </span>

                        <span
                          className={cn(
                            "flex h-8 w-8 items-center justify-center rounded-full text-xs transition",
                            active
                              ? "bg-white text-[#013E8E]"
                              : "bg-white/80 text-slate-500 ring-1 ring-slate-200 group-hover:translate-x-0.5 group-hover:text-[#013E8E] dark:bg-slate-900/80 dark:text-slate-300 dark:ring-slate-700",
                          )}
                        >
                          <FaArrowRight />
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl bg-white p-4 text-sm text-slate-500 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-400 dark:ring-slate-800">
              Aucun paramètre trouvé.
            </div>
          )}
        </div>
      </header>

      <main className="min-w-0 p-4 sm:p-5 lg:p-6">
        <div
          id="settings-active-panel"
          role="tabpanel"
          className="scroll-mt-28"
        >
          {ActiveComponent ? <ActiveComponent /> : null}
        </div>
      </main>
    </section>
  );
}
